/**
 * AI detection — the Phase 3 SHADOW report (design/MEDIA_EMBEDDINGS_PLAN.md §12.3).
 *
 * The detector scores every picture on the box and, while KV
 * `ai-detect:mode` is `shadow`, renders NOTHING to users. This screen is how a
 * person sees what it would have labelled: every would-be "Likely Made with
 * AI" with the picture, the raw score against the threshold, and the author's
 * OWN disclosure beside it.
 *
 * The agreement block is the headline. A label on a DISCLOSED post is the
 * detector agreeing with the author. A label on an UNDISCLOSED post is either
 * a catch (the dishonest case the feature exists for) or a false accusation —
 * and telling those apart by eye is exactly the work shadow mode exists to do
 * before the flip to live. Filter to "Undisclosed only" to review just those.
 *
 * Read-only by design: there is no action here. Disputes (§12.4) and the flip
 * to live are separate, later steps.
 */

import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { AdminShell } from '../components/AdminShell';
import { moderationApi, type ShadowReport } from '../lib/moderationApi';
import { EvidenceLightbox, type EvidenceItem } from '../components/EvidenceLightbox';
import { PageShell } from '../components/ui/PageShell';
import { TabBar } from '../components/ui/TabBar';

export const Route = createFileRoute('/ai-detect')({
  component: AiDetectRoute,
});

function AiDetectRoute() {
  return (
    <AdminShell mode="feature" featureName="AI detection">
      <ShadowConsole />
    </AdminShell>
  );
}

type Only = 'all' | 'label' | 'undisclosed';

// Inline, not Tailwind: a global heading rule outranked text-[11px] and the
// list kept its default indent (Q audit 2026-09-24).
const SECTION_H: React.CSSProperties = {
  margin: '24px 0 0',
  fontFamily: 'Inter, sans-serif',
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1.4,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--c-fg-tertiary)',
};
const LIST_RESET: React.CSSProperties = { listStyle: 'none', padding: 0, margin: '8px 0 0' };

function ShadowConsole() {
  const [only, setOnly] = useState<Only>('label');
  const [data, setData] = useState<ShadowReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  // This route is where a human decides whether a picture is AI-generated, so
  // it had the worst image affordance in the app: the post grid opened new
  // tabs and the avatar grid was completely inert — no anchor, no handler.
  // `posts` and `avatars` are separate navigable sets; paging from a post into
  // an avatar would be meaningless.
  const [lightbox, setLightbox] = useState<{ set: 'posts' | 'avatars'; index: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    moderationApi
      .aiDetectShadow(only, 120)
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'failed');
      });
    return () => {
      cancelled = true;
    };
  }, [only]);

  const s = data?.summary;
  const rate = (n: number, d: number) => (d ? `${((100 * n) / d).toFixed(1)}%` : '—');

  return (
    <PageShell
      title="AI detection"
      description="Every picture the detector scored, highest score first, with the author's own disclosure beside it. Whether labels show to users is set by the ai-detect:mode flag."
      maxWidth={1100}
    >

      {error ? (
        <p role="alert" className="mt-3 text-[13px]" style={{ color: 'var(--c-state-danger, #C7563E)' }}>
          {error}
        </p>
      ) : null}

      {data ? (
        <>
          {/* Head + summary */}
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="Head" value={data.head ? `${data.head.version} · thr ${data.head.threshold.toFixed(3)}` : '—'} sub={data.head?.model.split('/').pop() ?? ''} />
            <Stat label="Post pictures scored" value={String(s?.media_total ?? 0)} sub={`${s?.media_label ?? 0} would be labelled (${rate(s?.media_label ?? 0, s?.media_total ?? 0)})`} />
            <Stat label="Profile pictures scored" value={String(s?.avatar_total ?? 0)} sub={`${s?.avatar_label ?? 0} would be labelled (${rate(s?.avatar_label ?? 0, s?.avatar_total ?? 0)})`} />
            <Stat
              label="Agreement with authors"
              value={`${s?.label_disclosed ?? 0} / ${(s?.label_disclosed ?? 0) + (s?.label_undisclosed ?? 0)}`}
              sub={`labels on disclosed posts · ${s?.label_undisclosed ?? 0} on undisclosed · ${s?.silent_disclosed ?? 0} disclosed but missed`}
              tone={(s?.label_undisclosed ?? 0) > 0 ? 'warn' : undefined}
            />
          </div>

          {/* Filter */}
          <div className="mt-5">
            <TabBar<Only>
              label="Which pictures"
              activeKey={only}
              onSelect={setOnly}
              items={[
                { key: 'label', label: 'Would be labelled' },
                { key: 'undisclosed', label: 'Undisclosed only' },
                { key: 'all', label: 'Everything scored' },
              ]}
            />
          </div>

          {/* Post pictures */}
          <h2 style={SECTION_H}>
            Post pictures · by score
          </h2>
          {data.items.length === 0 ? (
            <p className="mt-2 text-[13px]" style={{ color: 'var(--c-fg-tertiary)' }}>
              Nothing in this view.
            </p>
          ) : (
            <ul className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5" style={LIST_RESET}>
              {data.items.map((it) => (
                <li key={it.media_id} className="overflow-hidden rounded-soft border border-hairline bg-surface">
                  <a
                    href={it.url ?? '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open post picture scored ${it.score.toFixed(2)}`}
                    onClick={(e) => {
                      if (!it.url || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                      e.preventDefault();
                      setLightbox({ set: 'posts', index: data.items.filter((x) => x.url).findIndex((x) => x.media_id === it.media_id) });
                    }}
                    className="block"
                    style={{ aspectRatio: '1 / 1', background: 'var(--c-bg-surface-alt)', cursor: it.url ? 'zoom-in' : 'default' }}
                  >
                    <Thumb url={it.url} />
                  </a>
                  <div className="px-2 py-1.5 text-[11px]" style={{ color: 'var(--c-fg-secondary)' }}>
                    <div className="flex items-center justify-between">
                      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: it.decided === 'label' ? '#9A4230' : 'var(--c-fg-tertiary)' }}>
                        {it.score.toFixed(2)}
                      </span>
                      <span style={{ fontWeight: 600, color: it.disclosed ? '#5C7A3D' : it.disclosed === false ? '#C7563E' : 'var(--c-fg-tertiary)' }}>
                        {it.disclosed ? 'disclosed' : it.disclosed === false ? 'NOT disclosed' : 'no post'}
                      </span>
                    </div>
                    <div className="mt-0.5 truncate">
                      {it.handle ? `@${it.handle}` : '—'}
                      {it.post_url ? (
                        <>
                          {' · '}
                          <a href={it.post_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--c-fg-link)' }}>
                            post
                          </a>
                        </>
                      ) : null}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Profile pictures */}
          <h2 style={SECTION_H}>
            Profile pictures · by score
          </h2>
          {data.avatars.length === 0 ? (
            <p className="mt-2 text-[13px]" style={{ color: 'var(--c-fg-tertiary)' }}>
              Nothing in this view.
            </p>
          ) : (
            <ul className="mt-2 grid grid-cols-4 gap-3 sm:grid-cols-6 md:grid-cols-8" style={LIST_RESET}>
              {data.avatars.map((a) => (
                <li key={a.user_id} className="text-center text-[11px]" style={{ color: 'var(--c-fg-secondary)' }}>
                  {/* Was a bare <div> — no anchor, no handler, completely
                      inert. A 72px circle is the LEAST reviewable thing on the
                      page, so it needed the viewer most. */}
                  <button
                    type="button"
                    disabled={!a.url}
                    aria-label={`Open profile picture for @${a.handle}, scored ${a.score.toFixed(2)}`}
                    onClick={() =>
                      setLightbox({ set: 'avatars', index: data.avatars.filter((x) => x.url).findIndex((x) => x.user_id === a.user_id) })
                    }
                    className="mx-auto block overflow-hidden rounded-full border border-hairline"
                    style={{ width: 72, height: 72, background: 'var(--c-bg-surface-alt)', padding: 0, cursor: a.url ? 'zoom-in' : 'default' }}
                  >
                    <Thumb url={a.url} small />
                  </button>
                  <div className="mt-1 truncate">@{a.handle}</div>
                  <div style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: a.decided === 'label' ? '#9A4230' : 'var(--c-fg-tertiary)' }}>
                    {a.score.toFixed(2)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : !error ? (
        <p className="mt-4 text-[13px]" style={{ color: 'var(--c-fg-tertiary)' }}>
          Loading…
        </p>
      ) : null}

      {lightbox && data ? (
        <EvidenceLightbox
          items={(lightbox.set === 'posts'
            ? data.items.filter((x) => x.url).map<EvidenceItem>((x) => ({
                url: x.url as string,
                label: `Post picture — scored ${x.score.toFixed(2)}${x.decided === 'label' ? ' · would be labelled' : ''}`,
                isImage: true,
              }))
            : data.avatars.filter((x) => x.url).map<EvidenceItem>((x) => ({
                url: x.url as string,
                label: `@${x.handle} — profile picture, scored ${x.score.toFixed(2)}${x.decided === 'label' ? ' · would be labelled' : ''}`,
                isImage: true,
              }))
          )}
          index={Math.max(0, lightbox.index)}
          onIndex={(next) => setLightbox({ set: lightbox.set, index: next })}
          onClose={() => setLightbox(null)}
        />
      ) : null}
    </PageShell>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'warn' }) {
  return (
    <div className="rounded-soft border border-hairline bg-surface px-3 py-2.5">
      <div className="text-[11px] font-semibold uppercase tracking-[0.04em]" style={{ color: 'var(--c-fg-tertiary)' }}>
        {label}
      </div>
      <div className="mt-0.5 text-[18px] font-semibold" style={{ fontVariantNumeric: 'tabular-nums', color: tone === 'warn' ? '#9A4230' : 'var(--c-fg-default)' }}>
        {value}
      </div>
      {sub ? (
        <div className="mt-0.5 text-[11px]" style={{ color: 'var(--c-fg-secondary)' }}>
          {sub}
        </div>
      ) : null}
    </div>
  );
}

/** A scored picture. The file can be gone (post deleted, media purged) while
 *  its score row remains, so a failed load says so instead of showing the
 *  browser's broken-image glyph (Q audit 2026-09-24). */
function Thumb({ url, small }: { url: string | null; small?: boolean }) {
  const [failed, setFailed] = useState(false);
  if (!url || failed) {
    return (
      <span
        style={{
          display: 'flex',
          width: '100%',
          height: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: 4,
          boxSizing: 'border-box',
          fontSize: small ? 9 : 11,
          color: 'var(--c-fg-tertiary)',
        }}
      >
        {small ? 'gone' : 'Image unavailable'}
      </span>
    );
  }
  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
    />
  );
}
